import { NavLink, Outlet } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  LayoutDashboard, CalendarDays, ClipboardList, Lightbulb, FileText, Camera,
  Users, TrendingUp, BarChart3, FlaskConical, BookOpen, CheckSquare, Settings, Upload, Sparkles, LogOut,
} from "lucide-react";

const NAV = [
  { to: "/", icon: LayoutDashboard, label: "Command Centre", end: true, id: "command-centre" },
  { to: "/monthly-plan", icon: ClipboardList, label: "Monthly Plan", id: "monthly-plan" },
  { to: "/calendar", icon: CalendarDays, label: "Calendar", id: "calendar" },
  { to: "/ideas", icon: Lightbulb, label: "Ideas", id: "ideas" },
  { to: "/scripts", icon: FileText, label: "Script Studio", id: "scripts" },
  { to: "/shots", icon: Camera, label: "Shoot Planner", id: "shots" },
  { to: "/competitors", icon: Users, label: "Competitors", id: "competitors" },
  { to: "/trends", icon: TrendingUp, label: "Trends", id: "trends" },
  { to: "/analytics", icon: BarChart3, label: "Analytics", id: "analytics" },
  { to: "/experiments", icon: FlaskConical, label: "Experiments", id: "experiments" },
  { to: "/knowledge", icon: BookOpen, label: "Knowledge Base", id: "knowledge" },
  { to: "/approvals", icon: CheckSquare, label: "Approvals", id: "approvals" },
  { to: "/import", icon: Upload, label: "Import Insights", id: "import" },
  { to: "/settings", icon: Settings, label: "Settings", id: "settings" },
];

export default function Layout() {
  const { user, workspaces, workspaceId, currentWorkspace, logout, switchWorkspace } = useAuth();

  const createDemo = async () => {
    try {
      const { data } = await api.post("/workspaces/demo");
      toast.success("Demo workspace created");
      localStorage.setItem("hop_workspace_id", data.workspace_id);
      window.location.reload();
    } catch (e) {
      toast.error("Failed to create demo");
    }
  };

  return (
    <div className="min-h-screen flex" style={{ background: "#F9F8F6", fontFamily: "'Satoshi', system-ui, sans-serif" }}>
      <aside className="w-64 border-r border-neutral-200 bg-white flex flex-col shrink-0">
        <div className="p-5 border-b border-neutral-200">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-md flex items-center justify-center" style={{ background: "#D4FF00" }}>
              <Sparkles className="w-5 h-5" strokeWidth={2} color="#1A1A1A" />
            </div>
            <div>
              <div className="font-black text-lg tracking-tight leading-none" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>HOP!</div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500">Content OS</div>
            </div>
          </div>
          {currentWorkspace && (
            <div className="mt-4">
              <select
                data-testid="workspace-selector"
                value={workspaceId || ""}
                onChange={(e) => switchWorkspace(e.target.value)}
                className="w-full text-sm border border-neutral-200 rounded-md px-2 py-1.5 bg-white"
              >
                {workspaces.map((w) => (
                  <option key={w.id} value={w.id}>{w.name}{w.is_demo ? " (Demo)" : ""}</option>
                ))}
              </select>
              {currentWorkspace.is_demo && (
                <div className="mt-2 text-[10px] font-semibold uppercase tracking-widest text-indigo-800 bg-indigo-100 border border-indigo-300 rounded px-1.5 py-0.5 inline-block">Demo data</div>
              )}
            </div>
          )}
        </div>
        <nav className="flex-1 py-3 overflow-y-auto">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`nav-${n.id}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-5 py-2 text-sm font-medium transition-colors ${
                  isActive ? "text-neutral-900 bg-neutral-100" : "text-neutral-600 hover:text-neutral-900 hover:bg-neutral-50"
                }`
              }
            >
              <n.icon className="w-4 h-4" strokeWidth={1.5} />
              <span>{n.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-neutral-200 space-y-2">
          <button
            data-testid="create-demo-btn"
            onClick={createDemo}
            className="w-full text-xs font-medium px-3 py-2 rounded border border-neutral-300 hover:bg-neutral-50 transition-colors"
          >
            Create demo workspace
          </button>
          {user && (
            <div className="flex items-center justify-between">
              <div className="text-xs text-neutral-600 truncate">{user.name}</div>
              <button data-testid="logout-btn" onClick={logout} className="text-neutral-500 hover:text-neutral-900 transition-colors">
                <LogOut className="w-4 h-4" strokeWidth={1.5} />
              </button>
            </div>
          )}
        </div>
      </aside>
      <main className="flex-1 overflow-y-auto">
        <Outlet />
      </main>
    </div>
  );
}
